import {QueryClient, QueryClientProvider} from "react-query";
import React, {useCallback, useState, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import {SystemRoutes} from "../../../Utils/RouteUtils";
import {HeaderMenu} from "../HeaderMenu";
import {
    ACard,
    ADataGrid,
    ADataGridColumn,
    ADataGridColors,
    ADataGridFilter,
    PagedSearchParams
} from "@atiautomacao/ati-ui-library";
import IconButton from "@mui/material/IconButton";
import {Tooltip, useTheme} from "@mui/material";
import {Edit} from "@mui/icons-material";
import CancelIcon from '@mui/icons-material/Cancel';
import ConfirmDialog from "../../../Shared/Components/ConfirmDialog";
import {DateFormat} from "../../../Utils/DateFormatPatternUtils";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import {CloseTicket, SearchTickets} from "./TicketReactQuery"
import {Ticket} from "../../../Shared/Types/Ticket";
import {useAppSelector} from "../../../Config/Hooks";
import {hasPermission} from "../../../Shared/Auth/AuthenticationUtil";
import {AUTHORITIES} from "../../../Config/Constants";

const initColumns = [
    {
        name: 'id',
        label: 'Numero',
        align: 'left',
        visibility: true,
        minWidth: 10,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'id', condition: 'contains', value: '', sort: 'desc'}

    },
    {
        name: 'status',
        label: 'Status',
        align: 'left',
        visibility: true,
        minWidth: 30,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'status', condition: 'contains', value: '', sort: ''}

    },
    {
        name: 'subject',
        label: 'Assunto',
        align: "left",
        visibility: true,
        minWidth: 150,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'subject', condition: 'equals', value: '', sort: ''}

    },
    {
        name: 'powerStationName',
        label: 'Usina',
        align: 'left',
        visibility: true,
        minWidth: 120,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'powerStationName', condition: 'contains', value: '', sort: ''}

    },
    {
        name: 'skidName',
        label: 'Skid',
        align: 'left',
        visibility: true,
        minWidth: 60,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'skidName', condition: 'contains', value: '', sort: ''}

    },
    {
        name: 'equipmentName',
        label: 'Equipamento',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'equipmentName', condition: 'contains', value: '', sort: ''}

    },
    {
        name: 'createdByName',
        label: 'Usuário',
        align: 'left',
        visibility: true,
        minWidth: 80,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'createdByName', condition: 'contains', value: '', sort: ''}

    },
    {
        name: 'scheduling',
        label: 'Agendamento',
        align: 'center',
        visibility: true,
        minWidth: 80,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'scheduling', condition: 'contains', value: '', sort: ''}

    },
] as ADataGridColumn [];

export type ActionConfig = {
    actionName: string;
    disabled: boolean;
};

export interface TicketRow {
    id: number;
    status: string;
    subject: string;
    description: string;
    type: string;
    categoryId: number;
    categoryName: string;
    subCategoryId: number;
    subCategoryName: string;
    powerStationId: number;
    powerStationName: string;
    skidId: number;
    skidName: string;
    equipmentId: number;
    equipmentName: string;
    createdByName: string;
    createdAt: Date;
    finishedByName: string;
    finishedAt: Date;
    actionsConfig?: Array<ActionConfig>;
}

export function TicketHome() {
    const theme = useTheme();
    const navigate = useNavigate();

    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToUpdate = hasPermission(account?.authoritySet, [ AUTHORITIES.SYSADMIN, AUTHORITIES.UPDATE_TICKET ]);
    const isAuthorizedToDelete = hasPermission(account?.authoritySet, [ AUTHORITIES.SYSADMIN, AUTHORITIES.DELETE_TICKET ]);

    const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(extractFiltersFromColumns(initColumns), 0, 50));
    const handleOnFiltersChange = useCallback((dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        let newPageParams: PagedSearchParams = new PagedSearchParams(dataGridFilters, page, pageSize);
        setPagedSearchParams(
            newPageParams
        );
    }, []);

    const [selectedToDelete, setSelectedToDelete] = useState<any>(null);
    const [totalOfRecords, setTotalOfRecords] = useState(0);
    const [rows, setRows] = useState<Array<TicketRow>>([]);
    const [openDialog, setOpenDialog] = useState(false);

    const { data, isSuccess } = SearchTickets(pagedSearchParams);
    const closeTicket = CloseTicket();

    useEffect(() => {
        if (isSuccess) {
            setTotalOfRecords(data?.totalOfRecords);
            if (data?.data) {
                let ticketRows = data.data.map((entity: Ticket) => {
                    return {
                        id: entity.id,
                        subject: entity.subject,
                        description: entity.description,
                        status: entity.status === 'CLOSED' ? 'FECHADO' : 'ABERTO',
                        type: entity.type,
                        categoryId: entity.categoryId,
                        categoryName: entity.categoryName,
                        subCategoryId: entity.subCategoryId,
                        subCategoryName: entity.subCategoryName,
                        powerStationId: entity.powerStationId,
                        powerStationName: entity.powerStationName,
                        skidId: entity.skidId,
                        skidName: entity.skidName,
                        equipmentId: entity.equipmentId,
                        equipmentName: entity.equipmentName,
                        scheduling: entity.scheduling ? DateFormat(`${entity.scheduling}T00:00:00`, 'dd/MM/yyyy') : "",
                        createdByName: entity.createdByName,
                        createdAt: entity.createdAt,
                        finishedByName: entity.finishedByName,
                        finishedAt: entity.finishedAt,
                        actionsConfig: [
                            {actionName: "edit", disabled: !isAuthorizedToUpdate},
                            {actionName: "delete", disabled: !isAuthorizedToDelete}
                        ]
                    };
                });
                setRows(ticketRows);
            }
        }
    }, [data]);

    const handleEdit = useCallback((data: any) => {
        navigate(SystemRoutes.SETTINGS_TICKET_FORM, { state: { id: data.id } });
    }, [navigate]);

    const handleDelete = useCallback((data: any) => {
        setSelectedToDelete(data);
        setOpenDialog(true);
    }, []);

    const handleDialogConfirm = async () => {
        closeTicket.mutate(selectedToDelete.id);
        setSelectedToDelete(null);
        setOpenDialog(!openDialog);
        setPagedSearchParams(new PagedSearchParams(extractFiltersFromColumns(initColumns), 0, 50));
    };

    const handleOnColor = (data: any, columnItem: any): ADataGridColors => {
        const colorTheme = theme?.palette?.mode === 'light' ? '#000000' : '#fff';
        const dataGridColors: ADataGridColors = {
            color: theme ? colorTheme : '#000000',
            backgroundColor: '',
        };
        if (columnItem.name === 'status') {
            dataGridColors.color = '#FFF';
            dataGridColors.backgroundColor = data.status === 'FECHADO' ? 'green' : 'orange';
        }
        return dataGridColors;
    };

    const isAuthorizedToTicketSave = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, 
        AUTHORITIES.SAVE_TICKET
    ]);

    const isAuthorizedToTicketSearch = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_TICKET, AUTHORITIES.VIEW_TICKET
    ]);

    const isAuthorizedToTicketCategorySearch = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_TICKET_CATEGORY, AUTHORITIES.VIEW_TICKET_CATEGORY
    ]);

    const isAuthorizedToTicketSubCategorySearch = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_TICKET_SUBCATEGORY, AUTHORITIES.VIEW_TICKET_SUBCATEGORY
    ]);

    const isAuthorizedToView = () => {

        if(isAuthorizedToTicketSave && isAuthorizedToTicketSearch && isAuthorizedToTicketCategorySearch && isAuthorizedToTicketSubCategorySearch ){
            return true;
        }
        return false;
    
    }

      const isAuthorizedToViewToUpdate = () => {

        if(isAuthorizedToUpdate && isAuthorizedToTicketSearch && isAuthorizedToTicketCategorySearch && isAuthorizedToTicketSubCategorySearch ){
            return true;
        }
        return false;
    
    }
    return(
        <>
            <HeaderMenu systemRoutes={SystemRoutes.SETTINGS_TICKET_FORM} disableSave={!isAuthorizedToView()}/>
            <ACard>
                <ADataGrid
                    hideFilters={false}
                    showFilterOptions={false}
                    hideSelection={false}
                    columns={initColumns}
                    loading={false}
                    rows={rows}
                    page={pagedSearchParams.page}
                    rowsPerPage={pagedSearchParams.size}
                    totalOfRecords={totalOfRecords}
                    onFiltersChange={handleOnFiltersChange}
                    onColorSelect={handleOnColor}
                    actions={
                        <>
                           <IconButton name="edit" size={"small"} aria-label="Edit" disabled={!isAuthorizedToViewToUpdate()} onClick={handleEdit}>
                               <Tooltip title="Editar">
                                   <Edit/>
                               </Tooltip>
                           </IconButton>
                           <IconButton name="delete" size={"small"} aria-label="Delete" disabled={!isAuthorizedToDelete} onClick={handleDelete}>
                               <Tooltip title="Fechar">
                                  <CancelIcon />
                               </Tooltip>
                           </IconButton>
                        </>
                    }
                />
            </ACard>
            <ConfirmDialog
                title={'Fechar'}
                description={'Deseja fechar esse ticket?'}
                open={openDialog}
                handleConfirm={handleDialogConfirm}
                handleClose={() => setOpenDialog(!openDialog)}
            />
        </>
    )
}

export default function TicketHomePage() {
    const queryClient = new QueryClient();

    return (
        <QueryClientProvider client={queryClient}>
            <TicketHome />
        </QueryClientProvider>
    )
}